import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { X, Trash2 } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addBranchToFrontendApp } from '../../../actions/infrastructures';
import Dropdown from '../../Dropdown';
import { BASE_URL } from '../../../config/api';

const BranchModal = ({ isOpen, onClose, appId, gitHubRepo }) => {
  const { projectId } = useParams();
  const [appBranchData, setAppBranchData] = useState({ 
    branchName: '',
    domain: '',
  });
  const [branches, setBranches] = useState([]);
  const [envVars, setEnvVars] = useState([{ key: '', value: '' }]);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRepoAndBranches = async () => {
      try {
        if (gitHubRepo) {
          const response = await fetch(`https://cloudify-prod.wizr.in/${gitHubRepo}/get_branches`);
          const branchesData = await response.json();
          setBranches(branchesData);
        }
      } catch (error) {
        console.error('Error fetching repo or branches:', error);
      }
    };
  
    if (isOpen) {
      fetchRepoAndBranches();
    }
  }, [appId, gitHubRepo, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const envVarObject = {};
    envVars.forEach(({ key, value }) => {
      if (key && value) {
        envVarObject[key] = value;
      }
    });
    
    await dispatch(addBranchToFrontendApp(projectId, appId, {
      ...appBranchData,
      envVars: envVarObject
    }));
    
    handleCancel();
  };

  const handleCancel = () => {
    setAppBranchData({ branchName: '', domain: '' });
    setEnvVars([{ key: '', value: '' }]);
    onClose();
  };

  const handleAddEnvVar = () => {
    setEnvVars([...envVars, { key: '', value: '' }]);
  };

  const handleRemoveEnvVar = (index) => {
    setEnvVars(envVars.filter((_, i) => i !== index));
  };

  const handleEnvVarChange = (index, field, value) => {
    const newEnvVars = [...envVars];
    newEnvVars[index][field] = value;
    setEnvVars(newEnvVars);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"></div>

      {/* Modal Container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl h-auto max-h-[90vh] transform overflow-hidden rounded-lg bg-white shadow-xl transition-all flex flex-col">
          {/* Fixed Header */}
          <div className="bg-gray-900 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
            <h3 className="text-lg font-medium text-white">
              Add New Branch
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Branch
                </label>
                <Dropdown
                  items={branches}
                  onSelect={(branch) => setAppBranchData({...appBranchData, branchName: branch})}
                  placeholder="Search branches..."
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Domain
                </label>
                <input
                  type="text"
                  value={appBranchData.domain}
                  onChange={(e) => setAppBranchData({...appBranchData, domain: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter domain"
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-2">
                  Environment Variables
                </label>
                <div className="space-y-3 mb-3">
                  {envVars.map((envVar, index) => (
                    <div key={index} className="flex space-x-2 items-center">
                      <input
                        type="text"
                        value={envVar.key}
                        onChange={(e) => handleEnvVarChange(index, 'key', e.target.value)}
                        placeholder="Key"
                        className="flex-1 px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <input
                        type="text"
                        value={envVar.value}
                        onChange={(e) => handleEnvVarChange(index, 'value', e.target.value)}
                        placeholder="Value"
                        className="flex-1 px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveEnvVar(index)}
                        className="p-2 text-red-600 hover:text-red-700"
                        aria-label="Remove environment variable"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddEnvVar}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
                >
                  + Add new
                </button>
              </div>
            </form>
          </div>

          {/* Fixed Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 sticky bottom-0 z-10">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Add Branch
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchModal;